import type { Router } from "vue-router";

import type { NotificationApi } from "@/Modules/Models/Api/ActivityCenter";
import { ACNotificationTypeApi } from "@/Modules/Models/Api/ActivityCenter";
import type { ACNotification } from "@/Modules/Models/Domain/ActivityCenter";
import { Role } from "@/Common/Models/Domain/Role";
import type { iconName } from "@/Modules/App/Models/Styles/Icons";

import { APP_SETTINGS_ROUTES, APP_CHAT_ROUTES } from "@/Generated/Routes/paths";

import useChatStore from "@/Modules/Chat/Store";

interface PartialNotificationParams {
  icon: iconName;
  role: Role;
  noAction?: boolean;
  isHide?: boolean;
}

export const setIconAndRole = (notification: NotificationApi): PartialNotificationParams => {
  switch (notification.event_type) {
    case ACNotificationTypeApi.SUBSCRIPTION_CANCELED_BY_USER:
      return {
        icon: "WarningCircle",
        role: Role.THERAPIST,
      };
    case ACNotificationTypeApi.SUBSCRIPTION_TERMINATED_BY_USER:
    case ACNotificationTypeApi.SUBSCRIPTION_TERMINATED_PAYMENT_FAILED:
    case ACNotificationTypeApi.SUBSCRIPTION_TERMINATED_REFUND_FAILED:
    case ACNotificationTypeApi.CLINIC_DISCHARGE_THERAPIST:
    case ACNotificationTypeApi.CLINIC_DISCHARGE:
      return {
        icon: "WarningCircle",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.FORM_RECEIVED_THERAPIST:
    case ACNotificationTypeApi.FORM_COMPLETED:
    case ACNotificationTypeApi.ASAP_ACCEPTED:
    case ACNotificationTypeApi.CI_ACCEPTED:
      return {
        icon: "Form",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.BREAK_ENDED_THERAPIST:
      return {
        icon: "PauseCircle",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.BREAK_CREATED:
    case ACNotificationTypeApi.BREAK_STARTED:
      return {
        icon: "PauseCircle",
        role: Role.THERAPIST,
        noAction: true,
      };

    case ACNotificationTypeApi.PLAN_SENT:
    case ACNotificationTypeApi.PLAN_CHANGED:
      return {
        icon: "ClipboardCheck",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.NEW_APPOINTMENT_THERAPIST:
    case ACNotificationTypeApi.SESSION_REGISTERED_THERAPIST:
      return {
        icon: "Calendar",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.APPOINTMENT_DELETED_THERAPIST:
    case ACNotificationTypeApi.SESSION_DELETED_THERAPIST:
      return {
        icon: "DeleteCalendar",
        role: Role.THERAPIST,
        noAction: true,
      };

    case ACNotificationTypeApi.PAYMENT_RECEIVED:
      return {
        icon: "PaymentMade",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.PAYMENT_ERROR_THERAPIST:
      return {
        icon: "PaymentError",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.ACCOUNT_DELETED:
      return {
        icon: "CloseCircle",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.NEW_REFERRAL_THERAPIST:
      return {
        icon: "Emoticons",
        role: Role.THERAPIST,
        noAction: true,
      };

    case ACNotificationTypeApi.PATIENT_REASSIGNED:
      return {
        icon: "PatientReassigned",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.SUBSCRIPTION_CANCELED_PAYMENT_FAILED:
      return {
        icon: "WarningCircle",
        role: Role.THERAPIST,
      };

    case ACNotificationTypeApi.FORM_RECEIVED_PATIENT:
      return {
        icon: "Form",
        role: Role.CUSTOMER,
      };

    case ACNotificationTypeApi.ASAP_RECEIVED:
    case ACNotificationTypeApi.CI_RECEIVED:
      return {
        icon: "Form",
        role: Role.CUSTOMER,
      };

    case ACNotificationTypeApi.BREAK_ENDED_PATIENT:
      return {
        icon: "PauseCircle",
        role: Role.CUSTOMER,
        noAction: true,
      };

    case ACNotificationTypeApi.NEW_PLAN_AVAILABLE:
      return {
        icon: "ClipboardCheck",
        role: Role.CUSTOMER,
      };

    case ACNotificationTypeApi.NEXT_APPOINTMENT:
    case ACNotificationTypeApi.NEW_APPOINTMENT_PATIENT:
    case ACNotificationTypeApi.SESSION_REGISTERED_CUSTOMER:
      return {
        icon: "Calendar",
        role: Role.CUSTOMER,
      };

    case ACNotificationTypeApi.APPOINTMENT_DELETED_PATIENT:
    case ACNotificationTypeApi.SESSION_DELETED_CUSTOMER:
      return {
        icon: "DeleteCalendar",
        role: Role.CUSTOMER,
      };

    case ACNotificationTypeApi.PAYMENT_MADE:
      return {
        icon: "PaymentMade",
        role: Role.CUSTOMER,
      };

    case ACNotificationTypeApi.PAYMENT_ERROR_PATIENT:
      return {
        icon: "PaymentError",
        role: Role.CUSTOMER,
      };

    case ACNotificationTypeApi.CHAT_END_IN_ONE_DAY:
      return {
        icon: "ChatEnd",
        role: Role.CUSTOMER,
      };

    case ACNotificationTypeApi.NEW_REFERRAL:
      return {
        icon: "Emoticons",
        role: Role.CUSTOMER,
        noAction: true,
      };

    case ACNotificationTypeApi.TRIAL_WEEK:
      return {
        icon: "TrialWeek",
        role: Role.CUSTOMER,
        noAction: true,
      };

    // this notification was a "wont do", at the moment. we have to hide it.
    case ACNotificationTypeApi.REFUND_DONE_THERAPIST:
    case ACNotificationTypeApi.REFUND_DONE_PATIENT:
    case ACNotificationTypeApi.REFUND_DONE_2_PATIENT:
    case ACNotificationTypeApi.RETURN_ORDER:
      return {
        icon: "TrialWeek",
        role: Role.CUSTOMER,
        noAction: true,
        isHide: true,
      };

    default:
      return {
        icon: "Therapy",
        role: Role.CUSTOMER,
      };
  }
};

const getRoomIdFromPatientUuid = (patientUuid: string): string | undefined => {
  const chatStore = useChatStore();

  return chatStore.getRoomIdFromPatientUuid(patientUuid);
};

const goToUserChat = (router: Router, patientUuid?: string) => {
  const chatStore = useChatStore();

  if (patientUuid) {
    const nextActiveRoom = getRoomIdFromPatientUuid(patientUuid);

    chatStore.setActiveChatRoom(nextActiveRoom);
    router.push({ name: APP_CHAT_ROUTES.root.name });
  }
};

const goToRoute = (router: Router, name: string) => {
  router.push({ name });
};

const goToPatientRoute = (
  router: Router,
  name: string,
  {
    customerId,
    quizId,
  }: {
    customerId: string;
    quizId?: string;
  }
) => {
  const nextActiveRoom = getRoomIdFromPatientUuid(customerId);
  const chatStore = useChatStore();

  chatStore.setActiveChatRoom(nextActiveRoom);

  const params = quizId ? { quizId } : { customerId };
  router.push({ name, params });
};

const goToPrivatePlans = (router: Router, planId?: string) => {
  if (planId) {
    router.push({ name: APP_SETTINGS_ROUTES.subscriptions.privatePlan.root.name, params: { planId } });
  }
};

export const doAction = (notification: ACNotification, router: Router): void => {
  switch (notification.eventType) {
    case ACNotificationTypeApi.SUBSCRIPTION_CANCELED_BY_USER:
    case ACNotificationTypeApi.SUBSCRIPTION_CANCELED_PAYMENT_FAILED:
    case ACNotificationTypeApi.BREAK_ENDED_THERAPIST:
    case ACNotificationTypeApi.PLAN_SENT:
      goToUserChat(router, notification.patientUuid);
      break;

    case ACNotificationTypeApi.SUBSCRIPTION_TERMINATED_PAYMENT_FAILED:
      goToRoute(router, APP_SETTINGS_ROUTES.subscriptions.payment.root.name);
      break;

    case ACNotificationTypeApi.FORM_RECEIVED_THERAPIST:
      goToRoute(router, APP_SETTINGS_ROUTES.questionnaires.root.name);
      break;

    case ACNotificationTypeApi.FORM_COMPLETED:
      goToPatientRoute(router, APP_CHAT_ROUTES.customer.questionnaires.answers.root.name, {
        customerId: notification.patientUuid,
        quizId: notification.metadata.userQuestionnaireUuid,
      });
      break;

    case ACNotificationTypeApi.ASAP_ACCEPTED:
      break;

    case ACNotificationTypeApi.CI_ACCEPTED:
      break;

    case ACNotificationTypeApi.PLAN_CHANGED:
      goToRoute(router, APP_SETTINGS_ROUTES.subscriptions.root.name);
      break;

    case ACNotificationTypeApi.NEW_APPOINTMENT_THERAPIST:
      goToPatientRoute(router, APP_CHAT_ROUTES.customer.breaks.root.name, {
        customerId: notification.patientUuid,
      });
      break;

    case ACNotificationTypeApi.APPOINTMENT_DELETED_THERAPIST:
      goToPatientRoute(router, APP_CHAT_ROUTES.customer.breaks.root.name, {
        customerId: notification.patientUuid,
      });
      break;

    case ACNotificationTypeApi.PAYMENT_RECEIVED:
      goToPatientRoute(router, APP_CHAT_ROUTES.customer.subscription.sessions.root.name, {
        customerId: notification.patientUuid,
      });
      break;

    case ACNotificationTypeApi.PAYMENT_ERROR_THERAPIST:
      goToUserChat(router, notification.patientUuid);
      break;

    case ACNotificationTypeApi.FORM_RECEIVED_PATIENT:
      goToRoute(router, APP_SETTINGS_ROUTES.questionnaires.root.name);
      break;

    case ACNotificationTypeApi.ASAP_RECEIVED:
    case ACNotificationTypeApi.CI_RECEIVED:
      break;

    case ACNotificationTypeApi.NEW_PLAN_AVAILABLE:
      goToPrivatePlans(router, notification.metadata.planUuid);
      break;

    case ACNotificationTypeApi.NEXT_APPOINTMENT:
    case ACNotificationTypeApi.NEW_APPOINTMENT_PATIENT:
    case ACNotificationTypeApi.APPOINTMENT_DELETED_PATIENT:
      goToRoute(router, APP_CHAT_ROUTES.root.name);
      break;

    case ACNotificationTypeApi.PAYMENT_MADE:
      goToRoute(router, APP_SETTINGS_ROUTES.subscriptions.sessions.root.name);
      break;

    case ACNotificationTypeApi.PAYMENT_ERROR_PATIENT:
      goToRoute(router, APP_SETTINGS_ROUTES.subscriptions.payment.root.name);
      break;

    case ACNotificationTypeApi.CHAT_END_IN_ONE_DAY:
      goToRoute(router, APP_CHAT_ROUTES.root.name);
      break;

    case ACNotificationTypeApi.REFUND_DONE_THERAPIST:
    case ACNotificationTypeApi.REFUND_DONE_PATIENT:
    case ACNotificationTypeApi.REFUND_DONE_2_PATIENT:
    case ACNotificationTypeApi.SESSION_DELETED_CUSTOMER:
    case ACNotificationTypeApi.SESSION_DELETED_THERAPIST:
    case ACNotificationTypeApi.SESSION_REGISTERED_CUSTOMER:
    case ACNotificationTypeApi.SESSION_REGISTERED_THERAPIST:
      goToRoute(router, APP_SETTINGS_ROUTES.subscriptions.sessions.root.name);
      break;

    case ACNotificationTypeApi.NEW_REFERRAL:
    case ACNotificationTypeApi.RETURN_ORDER:
    case ACNotificationTypeApi.ACCOUNT_DELETED:
    case ACNotificationTypeApi.NEW_REFERRAL_THERAPIST:
    case ACNotificationTypeApi.PATIENT_REASSIGNED:
    case ACNotificationTypeApi.SUBSCRIPTION_TERMINATED_BY_USER:
    case ACNotificationTypeApi.SUBSCRIPTION_TERMINATED_REFUND_FAILED:
    case ACNotificationTypeApi.BREAK_CREATED:
    case ACNotificationTypeApi.BREAK_STARTED:
    case ACNotificationTypeApi.BREAK_ENDED_PATIENT:
    case ACNotificationTypeApi.TRIAL_WEEK:
      break;

    default:
      goToRoute(router, APP_CHAT_ROUTES.root.name);
  }
};
